import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ValuePlaceholder } from '@openx/components-core/lib/Value/ValuePlaceholder/ValuePlaceholder.tsx';
import { ReactQueryStaleTime } from '@openx/constants/react-query.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { abbreviatedNumber } from '@openx/utils/lib/abbreviatedNumber.ts';
import { numberWithCommas } from '@openx/utils/lib/numberWithCommas.ts';
import { useQuery } from '@tanstack/react-query';
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetAudiencesListQuery } from '../../../../../graphql/query/audiences/GetAudiencesList.generated.tsx';
import { GetExportsByAudienceDocument } from '../../../../../graphql/query/audiences/GetExportsByAudienceId.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../../../modules/graphql/types.ts';
import { PROCESSING_STATUSES } from '../../constants.ts';
import { getSectionTypeToNameMap } from '../../utils/getSectionTypeToNameMap.ts';

type ReachCellProps = {
  audience: GetAudiencesListQuery['audience'][number];
};

export const ReachCell = ({ audience }: ReachCellProps) => {
  const { t } = useTranslation();

  if (!(PROCESSING_STATUSES as readonly string[]).includes(audience.status) && !audience.addressable_reach) {
    return <ValuePlaceholder />;
  }

  const label = audience.addressable_reach ? abbreviatedNumber(audience.addressable_reach) : t('Pending');

  return (
    <div>
      <span>{label}</span>
      <Tooltip title={<TooltipContent id={audience.id} reach={audience.addressable_reach} />} placement="right">
        <IconButton size="large">
          <InfoRoundedIcon />
        </IconButton>
      </Tooltip>
    </div>
  );
};

type TooltipContentProps = {
  id: string;
  reach?: number | null;
};

export const TooltipContent = ({ id, reach }: TooltipContentProps): JSX.Element => {
  const { t } = useTranslation();
  const { isLoading: loading, data } = useQuery({
    queryKey: [QueryKeys.AUDIENCE, id, 'exports'],
    queryFn: ({ client }) => getGraphQLRequestFromClient(client)(GetExportsByAudienceDocument, { id }),
    enabled: !!reach,
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  const sectionsNameLabels = useMemo(() => getSectionTypeToNameMap(t), [t]);

  if (!reach) {
    return (
      <>
        {t(
          'Your audience is currently processed by our services and its reach is calculated. It may take up to few hours for audience reach to be estimated.'
        )}
      </>
    );
  }

  if (loading) {
    return (
      <Box sx={{ width: 240 }}>
        <Skeleton animation="wave" width={50} />
        <Skeleton animation="wave" height={60} />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
        <Skeleton animation="wave" />
      </Box>
    );
  }

  const reachData = data?.audience_by_pk?.exports?.[0].export_id_types || [];

  return (
    <Box sx={{ width: 240 }}>
      <Typography variant="body1Bold" gutterBottom data-test="reach-title">
        {t('Breakdown')}
      </Typography>
      <Typography
        variant="body2"
        gutterBottom
        data-test="reach-description"
        sx={{
          mb: 2,
        }}
      >
        {t(
          'The estimated monthly reach of {reach} unique users is associated with the following cookie, device & id counts:',
          { reach: abbreviatedNumber(reach) }
        )}
      </Typography>
      <Typography variant="body2Bold" data-test="reach-values">
        {reachData.map(({ id_type, id_count }) => (
          <Box key={`${id_type}.${id_count}`} sx={{ height: 20 }}>
            {sectionsNameLabels[id_type] ?? id_type}:{' '}
            {typeof id_count === 'number' ? numberWithCommas(id_count) : <ValuePlaceholder />}
          </Box>
        ))}
      </Typography>
    </Box>
  );
};
